import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import {
  AddressInputComponent,
  InformationBoxComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  InputSearchFilter,
  NgCommonModule,
  toSearchStream,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  ACC_BOOLEAN_DISPLAY_MAP,
  ACC_EMPLOYMENT_STATUS_MAP,
  ACC_OCCUPATION_CODE_UNEMPLOYED,
  ACC_OCCUPATION_ENTRIES,
  ACC_WORK_TYPE_MAP,
  AccBooleanValue,
  AccOccupationCode,
  IACCOccupationDisplayEntry,
  IAddressMetadata,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  ISelectOption,
  toOptions,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { of } from 'rxjs';
import { AccEmploymentFormGroup } from '../../form-groups/acc-employment.formgroup';

@Component({
  selector: 'pr-acc-employment-form',
  imports: [
    NgCommonModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatDividerModule,
    MatAutocompleteModule,
    AddressInputComponent,
    InformationBoxComponent,
  ],
  templateUrl: './acc-employment-form.component.html',
  styleUrls: ['./acc-employment-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccEmploymentFormComponent {
  form = input.required<AccEmploymentFormGroup>();
  canResumeNormalWork = input<AccBooleanValue | undefined>(undefined);
  disabled = input<boolean>(false);
  addressCtrl = new TypedFormControl<IAddressMetadata>({ address: '' });
  occupationSearchCtrl = new TypedFormControl<string>('', [
    Validators.required,
  ]);
  occupationSearch: InputSearchFilter<IACCOccupationDisplayEntry>;

  readonly occupationEntries = ACC_OCCUPATION_ENTRIES;
  readonly employmentStatusOptions = toOptions(ACC_EMPLOYMENT_STATUS_MAP);
  readonly workTypeOptions = toOptions(ACC_WORK_TYPE_MAP);
  readonly booleanOptions = toOptions(AccBooleanValue);
  readonly booleanDisplay = ACC_BOOLEAN_DISPLAY_MAP;

  constructor() {
    toObservable(this.form)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((form) => {
        if (form.controls.occupationCode.value) {
          this.occupationSearchCtrl.setValue(
            form.controls.occupationCode.value.label,
            { emitEvent: false }
          );
        }
      });

    this.addressCtrl.valueChanges
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((value) => {
        this.form().employer.address.convertAddressComponents(value.components);
      });

    toObservable(this.canResumeNormalWork)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((value) => {
        this.form().canResumeNormalWork.set(value);
      });

    this.occupationSearch = new InputSearchFilter(
      of(this.occupationEntries),
      toSearchStream(this.occupationSearchCtrl),
      ['displayName', 'searchTerms']
    );

    toObservable(this.disabled)
      .pipe(takeUntilDestroyed())
      .subscribe((disabled) => {
        if (disabled) {
          this.addressCtrl.disable({ emitEvent: false });
          this.occupationSearchCtrl.disable({ emitEvent: false });
        } else {
          this.addressCtrl.enable({ emitEvent: false });
          this.occupationSearchCtrl.enable({ emitEvent: false });
        }
      });
  }

  displayFn(occupation: IACCOccupationDisplayEntry | string): string {
    if (isString(occupation)) {
      const foundOccupation = this.occupationEntries?.find(
        (entry) => entry.code === occupation
      );
      return foundOccupation ? foundOccupation.displayName : occupation;
    }
    return occupation?.displayName || '';
  }

  inPaidEmploymentSelected(
    event: MatRadioChange,
    formGroup: AccEmploymentFormGroup
  ): void {
    const selectedValue = event.value as AccBooleanValue;
    if (selectedValue === AccBooleanValue.False) {
      this._updateOccupationControl(
        {
          label: 'Unemployed',
          value: ACC_OCCUPATION_CODE_UNEMPLOYED,
        },
        formGroup
      );
    }
  }

  occupationSelected(
    event: MatAutocompleteSelectedEvent,
    formGroup: AccEmploymentFormGroup
  ): void {
    const selectedOccupation = event.option.value as IACCOccupationDisplayEntry;
    const { code, displayName } = selectedOccupation;
    this._updateOccupationControl(
      { label: displayName, value: code },
      formGroup
    );
  }

  private _updateOccupationControl(
    value: ISelectOption<AccOccupationCode>,
    formGroup: AccEmploymentFormGroup
  ): void {
    formGroup.controls.occupationCode.setValue(value);
    this.occupationSearchCtrl.setValue(value.label);
  }
}
